import { html } from "lit";
import { customElement, property, queryAll } from "lit/decorators.js";
import { map } from "lit/directives/map.js";
import { style } from "./sectionVisualization.style";
import { VisualizationConfig } from "../../types";
import { dom, IconSpec, utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { BaseSection } from "./sectionBase";
import { isCustomViewType } from "../../common";
import rp from "@reporting/rp-app-host";

type VisualizationItem = VisualizationConfig & {_index?: number};

@customElement('rp-fsection-visualization')
export class VisualizationSection extends BaseSection {

	static styles = [...BaseSection.styles, style];

	@property({type: Array})
	standardItems: VisualizationItem[] = [];

	@property({type: String})
	type: 'standard'|'custom' = 'standard';

	@property({type: Array})
	customItems: VisualizationItem[] = [];

	// @property({type: Number})
	_selectedIndex = 0;
	get selectedIndex() { return this._selectedIndex }
	set selectedIndex(value: number) { this._selectedIndex = value }

	@property({type: String})
	selectedType: 'standard'|'custom';

	@queryAll('.item')
	itemElements: NodeListOf<HTMLDivElement>;

	get items() { return this.type == 'standard' ? this.standardItems : this.customItems }

	get selectedItem() { return this.items[this.selectedIndex] }

	getItemIcon(item: VisualizationItem) {
		const icon = (utils.isObject(item.icon) ? item.icon : {icon: item.icon || 'puzzle-piece'}) as IconSpec;
		icon.size = 'lg';
		return icon;
	}

	willUpdate(props: Map<string, unknown>) {
		if (props.has('context') && this.context) {
			this.type = isCustomViewType(this.context.view) || !this.standardItems?.length ? 'custom' : 'standard';
			this.selectedType = this.type;
			this.selectedIndex = this.type == 'standard'
				? this.standardItems.findIndex(item => item.id == this.context.view)
				: this.customItems.findIndex(item => item.id == this.context.view);
		}

		if (this.selectedType == null) {
			this.selectedType = this.type;
		}
	}

	updated() {
		this.itemElements.forEach((el) => {
			const wrapper = el.querySelector('.item-name-wrapper') as HTMLElement;
			const name = wrapper.firstElementChild;
			if (!name) return;
			const overflow = name.scrollWidth - wrapper.clientWidth;
			if (overflow > 4) { // 4px tolerance
				el.classList.add('long-name');
				const shift = dom.px(-overflow - 5);
				wrapper.style.setProperty('--shift', shift);
			}
		})
	}

	selectItem(index: number) {
		this.selectedType = this.type;
		this.selectedIndex = index;
		this.requestUpdate();
		this.updateContext('view_options', {});
		this.updateContext('view', this.selectedItem.id);
	}

	previewItem(index: number) {
		if (this.type == 'standard') {
			this.previewContext('view', this.standardItems[index].id);
		}
	}

	_onItemMouseEnter = (e: MouseEvent, index: number) => {
		this.previewItem(index);
	}

	_onItemMouseLeave = () => {
		this.endPreview();
	}

	renderStandardItems = () => map(this.standardItems, (item, i) => html`
		<div
			class="item${i == this.selectedIndex && this.selectedType == 'standard' ? ' active' : ''} item-default"
			@click="${() => this.selectItem(i)}"
			@mouseenter="${(e: MouseEvent) => this._onItemMouseEnter(e, i)}"
			@mouseleave="${() => this._onItemMouseLeave()}">
			<og-icon .icon="${this.getItemIcon(item)}"></og-icon>
			<div class="item-name-wrapper">
				<div class="item-name">${msg(item.name)}</div>
			</div>
		</div>
	`);

	renderCustomItems = () => map(this.customItems, (item, i) => html`
		<div
			class="item${i == this.selectedIndex && this.selectedType == 'custom' ? ' active' : ''} item-custom"
			@click="${() => this.selectItem(i)}"
			@mouseenter="${(e: MouseEvent) => this._onItemMouseEnter(e, i)}"
			@mouseleave="${() => this._onItemMouseLeave()}">
			<og-icon .icon="${this.getItemIcon(item)}"></og-icon>
			<div class="item-name-wrapper">
				<div class="item-name">${msg(item.name)}</div>
			</div>

			${item.resource ? html`
				<og-dropdown-menu
					dynamicWidth
					nocaret
					icon="ellipsis"
					color="transparent"
					.items="${[
						{k: 'resource', v: `#${item.resource.id_custom_resource} ${item.resource.name}`, exec: () => 
							rp.host.open(`extensions-file-${item.resource.id_custom_resource}`, {title: item.resource.name})},
						{k: 'extension', v: `#${item.resource.id_custom_page} extension`, exec: async () => {
							await rp.host.openSidebarComponent('extensions');
							const sidebar = await rp.host.components.get('extensions') as any;
							sidebar.openProject(item.resource.id_custom_page)
						}},
					]}"
					@click="${(e: MouseEvent) => e.stopPropagation()}">
				</og-dropdown-menu>	
			` : ''}
		</div>
	`);

	render = () => html`
		${this.standardItems?.length && this.customItems?.length ? html`
			<div class="head">
				<div class="head-inner">
					<og-bucket-button
						icon="chart-tree-map"
						.text="${msg('STANDARD')}"
						@click="${() => this.type = 'standard'}"
						?active="${this.type == "standard"}">
					</og-bucket-button>

					<og-bucket-button
						icon="puzzle-piece"
						.text="${msg('CUSTOM')}"
						@click="${() => this.type = 'custom'}"
						?active="${this.type == "custom"}">
					</og-bucket-button>
				</div>
			</div>
		` : ''}

		<div class="body">
			${this.type == 'standard' ? this.renderStandardItems() : this.renderCustomItems()}
		</div>
	`;

}
